import { createTheme } from '@mui/material/styles'
import { getLocalStorage } from './utils/helper'
import { buildPrimaryPalette } from './utils/theming/buildPrimaryPalette'
import { applyPrimaryCssVariables } from './utils/theming/applyPrimaryCssVariables'

// Fallback brand color when no tenant theme is configured yet (matches the previous default).
const DEFAULT_BRAND_COLOR = '#456DB3'

const getStoredTheme = () => {
  const rawTheme = getLocalStorage('theme')
  if (!rawTheme) return null

  try {
    return JSON.parse(rawTheme)
  } catch {
    return null
  }
}

const localStorageTheme = getStoredTheme()
const tenantColor = localStorageTheme?.colors?.primary

// Only the tenant's primary color is read now — theme_json.colors.secondary (if the backend
// still sends it) is ignored. Everything below is derived from this one color.
const primaryPalette = buildPrimaryPalette(tenantColor || DEFAULT_BRAND_COLOR)

// Mirrors the same derived palette into CSS variables so Tailwind's `primary-*` classes
// (tailwind.config.js) render the identical colors as MUI, from this one computation.
applyPrimaryCssVariables(primaryPalette)

const theme = createTheme({
  typography: {
    fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
    h1: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.025em' },
    h2: { fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.02em' },
    h3: { fontSize: '1.125rem', fontWeight: 650, lineHeight: 1.4 },
    h4: { fontSize: '1rem', fontWeight: 650, lineHeight: 1.45 },
    body1: { fontSize: '0.875rem', lineHeight: 1.55 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    button: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none' },
  },
  palette: {
    // `tones` below isn't part of MUI's PaletteColorOptions type; cast avoids fighting
    // the type-checker over the custom field, matching this codebase's `as any` convention.
    primary: {
      main: primaryPalette.main,
      light: primaryPalette.light,
      dark: primaryPalette.dark,
      contrastText: primaryPalette.contrastText,
      // Full generated tonal ramp for anything beyond main/light/dark, e.g.
      // `theme.palette.primary.tones[50]` for a pale surface fill.
      tones: primaryPalette.tones,
    },
    // No `secondary` palette is configured — nothing in the app references it (no
    // `color="secondary"` MUI prop usage, no `secondary.*` sx references; all were
    // migrated onto `primary`/`primary.tones`). MUI falls back to its own default
    // purple internally, but it's never actually rendered anywhere.
    error: {
      main: '#dc2626',
    },
    success: {
      main: '#059669',
    },
    warning: {
      main: '#d97706',
    },
    background: {
      default: '#f8f9fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#111827',
      secondary: '#5f6775',
      disabled: '#9ca3af',
    },
    divider: '#e2e6ee',
    common: {
      black: '#000',
    },
  } as any,
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(17, 24, 39, 0.06), 0 1px 2px rgba(17, 24, 39, 0.04)',
    '0 2px 8px rgba(17, 24, 39, 0.07), 0 1px 3px rgba(17, 24, 39, 0.04)',
    '0 4px 16px rgba(17, 24, 39, 0.08), 0 2px 4px rgba(17, 24, 39, 0.04)',
    ...Array(21).fill('0 8px 28px rgba(17, 24, 39, 0.10)'),
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#f8f9fb', color: '#111827' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: 8,
          paddingInline: 16,
          boxShadow: 'none',
          transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 120ms ease',
          '&:active': { transform: 'scale(0.985)' },
        },
        contained: {
          '&:hover': { boxShadow: '0 2px 8px rgba(17, 24, 39, 0.10)' },
        },
        outlined: {
          borderColor: '#e2e6ee',
          backgroundColor: '#fff',
        },
      },
    },
    // Selected page sits on a primary.main fill, so its text must use the contrast colour derived
    // from that same fill (black for pale brand colours, white otherwise) — not a hardcoded white.
    MuiPaginationItem: {
      styleOverrides: {
        root: { '&.Mui-selected': { color: primaryPalette.contrastText } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 12 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 42,
          borderRadius: 8,
          backgroundColor: '#fff',
          '& fieldset': { borderColor: '#e2e6ee' },
          '&:hover fieldset': { borderColor: '#c5ccd8' },
          '&.Mui-focused fieldset': { borderWidth: '1px' },
        },
        input: { padding: '10px 12px', fontSize: '0.875rem' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '0.875rem' } },
    },
    MuiChip: {
      styleOverrides: {
        root: { height: 26, borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16, border: '1px solid #e2e6ee' } },
    },
    MuiMenu: {
      styleOverrides: { paper: { border: '1px solid #e2e6ee', boxShadow: '0 8px 24px rgba(17, 24, 39, 0.12)' } },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { borderRadius: 6, fontSize: '0.75rem' } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: '#edf0f5' },
        head: { color: '#6b7280', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },
        body: { color: '#111827', fontSize: '0.8125rem' },
      },
    },
  },
}) as any

export default theme
